import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  TextField, 
  InputAdornment, 
  IconButton,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip
} from '@mui/material';
import { 
  Search, 
  PersonAdd, 
  Email, 
  Phone, 
  Business, 
  Description,
  Schedule,
  CheckCircle,
  Warning
} from '@mui/icons-material';

const UnregisteredCandidates = ({ companyName }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchUnregisteredCandidates();
  }, [currentPage, searchTerm]);

  const fetchUnregisteredCandidates = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('gogig_token');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: ITEMS_PER_PAGE.toString(),
        search: searchTerm
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/company/unregistered-candidates?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setCandidates(data.candidates);
        setTotalPages(data.totalPages);
        setTotalCount(data.total);
      } else {
        setError(data.message || 'Failed to fetch unregistered candidates');
      }
    } catch (err) {
      console.error('Error fetching unregistered candidates:', err);
      
      // Handle connection errors
      if (err.message === 'Failed to fetch' || err.name === 'TypeError' || err.message.includes('ERR_CONNECTION_REFUSED')) {
        setError('Cannot connect to server. Please ensure the backend server is running on port 5001.');
        setCandidates([]);
      } else if (err.message.includes('401') || err.message.includes('403') || err.message.includes('Invalid token') || err.message.includes('Unauthorized')) {
        localStorage.removeItem('gogig_token');
        localStorage.removeItem('gogig_user');
        setError('Session expired. Please log in again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        setCandidates([]);
      } else if (err.message.includes('404')) {
        setError('No unregistered candidates found.');
        setCandidates([]);
      } else {
        setError('Failed to fetch unregistered candidates: ' + (err.message || 'Unknown error occurred'));
        setCandidates([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'NAPS': return '#22c55e';
      case 'BVOC': return '#3498db';
      case 'Salaried': return '#f39c12';
      case 'NATS': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getDocumentIcon = (documentType) => {
    switch (documentType) {
      case 'contract': return '📄';
      case 'stipend': return '💰';
      case 'salary': return '💵';
      case 'bvoc_salary': return '🏢';
      case 'nats_stipend': return '🎓';
      default: return '📋';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && candidates.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
          Unregistered Candidates
        </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280', mb: 3 }}>
          Candidates with uploaded documents who haven't registered yet
        </Typography>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search candidates by name, email, or mobile..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#9ca3af' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 500,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: '#f9fafb',
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
              '&.Mui-focused': {
                backgroundColor: '#ffffff',
                boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.1)',
              },
            },
          }}
        />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totalCount}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Unregistered
                  </Typography>
                </Box>
                <PersonAdd sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(240, 147, 251, 0.3)'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {candidates.filter(c => c.document_type === 'contract').length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Contract Documents
                  </Typography>
                </Box>
                <Description sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(79, 172, 254, 0.3)'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {candidates.filter(c => c.document_type === 'stipend').length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Stipend Documents
                  </Typography>
                </Box>
                <Business sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Candidates Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Candidate</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Document Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Upload Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map((candidate) => (
                <TableRow 
                  key={candidate.id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: '#f8fafc',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ 
                        bgcolor: getTypeColor(candidate.type),
                        width: 40,
                        height: 40,
                        fontSize: '1rem',
                        fontWeight: 600
                      }}>
                        {candidate.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f2937' }}>
                          {candidate.name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                          <Email sx={{ fontSize: 14, color: '#9ca3af' }} />
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {candidate.email || 'No email'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Phone sx={{ fontSize: 14, color: '#9ca3af' }} />
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {candidate.mobile || 'No mobile'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone sx={{ fontSize: 16, color: '#9ca3af' }} />
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {candidate.mobile || 'No mobile'}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                        {getDocumentIcon(candidate.document_type)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280', textTransform: 'capitalize', fontWeight: 600 }}>
                        {candidate.document_type.replace('_', ' ')}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Schedule sx={{ fontSize: 16, color: '#9ca3af' }} />
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {formatDate(candidate.created_at)}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      icon={<Warning sx={{ fontSize: 16 }} />}
                      label="Pending Registration"
                      sx={{
                        backgroundColor: '#fef3c7',
                        color: '#d97706',
                        fontWeight: 600,
                        borderRadius: 2
                      }}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Tooltip title="Send registration reminder">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonAdd />}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: '#3b82f6',
                          color: '#3b82f6',
                          '&:hover': {
                            backgroundColor: '#3b82f6',
                            color: 'white'
                          }
                        }}
                      >
                        Remind
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" p={3}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                  fontWeight: 600
                }
              }}
            />
          </Box>
        )}

        {/* Empty State */}
        {candidates.length === 0 && !loading && (
          <Box textAlign="center" py={8}>
            <PersonAdd sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
              No unregistered candidates found
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              All candidates from your uploaded documents have registered
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default UnregisteredCandidates;
